import React, { useState, useEffect, useRef } from "react";

const date = new Date();
const year = date.getFullYear();

const isLeapYear = (year) => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

const getMaxDays = (year, month) => {
  if (month === 2) {
    return isLeapYear(year) ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
};

const ITEM_HEIGHT = 40;
const VISIBLE_COUNT = 5;

const PickerColumn = ({ options, value, onChange }) => {
  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const dragging = useRef(false);
  const startY = useRef(0);
  const startTY = useRef(0);
  const curY = useRef(0);
  const lastTouchY = useRef(0);
  const lastTime = useRef(0);
  const vel = useRef(0);
  const rafId = useRef(null);

  const selectedIndex = options.indexOf(value);
  const yForIndex = (i) => -i * ITEM_HEIGHT;
  const minY = yForIndex(options.length - 1);
  const maxY = yForIndex(0);
  const clamp = (y) => Math.max(minY, Math.min(maxY, y));

  const setTx = (y) => {
    if (innerRef.current) {
      innerRef.current.style.transform = `translate3d(0,${y}px,0)`;
    }
  };

  const snapTo = (fromY, momentum = false) => {
    cancelAnimationFrame(rafId.current);

    let target = fromY;
    if (momentum) {
      target = fromY + vel.current * 150;
    }

    target = clamp(target);
    const idx = Math.round(-target / ITEM_HEIGHT);
    const ci = Math.max(0, Math.min(idx, options.length - 1));
    const finalY = yForIndex(ci);

    const dist = finalY - fromY;
    const dur = Math.min(400, Math.max(150, Math.abs(dist) * 2));
    const t0 = performance.now();

    const animate = (now) => {
      const p = Math.min((now - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = fromY + dist * eased;
      curY.current = v;
      setTx(v);

      if (p < 1) {
        rafId.current = requestAnimationFrame(animate);
      } else {
        curY.current = finalY;
        setTx(finalY);
        if (options[ci] !== value) {
          onChange(options[ci]);
        }
      }
    };

    rafId.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    if (!dragging.current) {
      cancelAnimationFrame(rafId.current);
      const y = yForIndex(selectedIndex);
      curY.current = y;
      setTx(y);
    }
  }, [selectedIndex, options.length]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onStart = (e) => {
      e.stopPropagation();
      cancelAnimationFrame(rafId.current);
      dragging.current = true;
      startY.current = e.touches[0].clientY;
      startTY.current = curY.current;
      lastTouchY.current = e.touches[0].clientY;
      lastTime.current = performance.now();
      vel.current = 0;
    };

    const onMove = (e) => {
      if (!dragging.current) return;
      e.preventDefault();
      e.stopPropagation();

      const ty = e.touches[0].clientY;
      const now = performance.now();
      const dt = now - lastTime.current;

      if (dt > 0) {
        vel.current = (ty - lastTouchY.current) / dt * 16;
      }

      lastTouchY.current = ty;
      lastTime.current = now;

      let newY = startTY.current + (ty - startY.current);
      newY = clamp(newY);

      curY.current = newY;
      setTx(newY);
    };

    const onEnd = (e) => {
      if (!dragging.current) return;
      e.stopPropagation();
      dragging.current = false;
      snapTo(curY.current, true);
    };

    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: false });

    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
    };
  }, [options, value, onChange]);

  useEffect(() => () => cancelAnimationFrame(rafId.current), []);

  return (
    <div
      ref={containerRef}
      style={{
        height: ITEM_HEIGHT * VISIBLE_COUNT,
        overflow: 'hidden',
        flex: 1,
        position: 'relative',
        touchAction: 'none',
        WebkitOverflowScrolling: 'auto',
        overscrollBehavior: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      <div
        ref={innerRef}
        style={{
          position: 'absolute',
          top: ITEM_HEIGHT * 2,
          left: 0,
          right: 0,
          willChange: 'transform',
        }}
      >
        {options.map((option) => {
          const isSelected = option === value;
          return (
            <div
              key={option}
              onClick={() => {
                const idx = options.indexOf(option);
                onChange(option);
                snapTo(yForIndex(idx));
              }}
              style={{
                height: ITEM_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isSelected ? '24px' : '18px',
                fontWeight: '700',
                color: isSelected ? '#10141A' : '#D2D6DC',
                transition: 'font-size 0.2s, color 0.2s',
                cursor: 'pointer',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
              {option}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CustomDatePicker = ({ onClose, type, titleTxt, subTitleTxt, nextOpen, children, onConfirm, selectedDate }) => {
  const initialDate = selectedDate ? new Date(selectedDate) : new Date();
  const initialYear = initialDate.getFullYear();
  const initialMonth = initialDate.getMonth() + 1;
  const initialDay = initialDate.getDate();

  const [value, setValue] = useState({
    year: initialYear,
    month: initialMonth,
    day: initialDay
  });

  const [dayOptions, setDayOptions] = useState(
    Array.from({ length: 31 }, (_, i) => i + 1)
  );

  const closeButtonRef = useRef(null);

  useEffect(() => {
    const maxDays = getMaxDays(value.year, value.month);
    setDayOptions(Array.from({ length: maxDays }, (_, i) => i + 1));

    if (value.day > maxDays) {
      setValue((prev) => ({ ...prev, day: maxDays }));
    }
  }, [value.year, value.month]);

  useEffect(() => {
    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, []);

  const yearOptions = Array.from({ length: 24 }, (_, i) => i + Number(year) - 12);
  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  const handleConfirm = () => {
    const selectedDate = new Date(value.year, value.month - 1, value.day);
    onConfirm && onConfirm(selectedDate);
    nextOpen && nextOpen();
    onClose && onClose();
  };

  return (
    <div className="date-picker-wrap">
      {type ? children : null}

      <strong className="picker-title">{titleTxt}</strong>
      <span>{subTitleTxt}</span>

      <div className="date-picker-box">
        <div className="picker-sub-txt">
          <span>년</span>
          <span>월</span>
          <span>일</span>
        </div>

        <div
          style={{
            display: "flex",
            width: "70%",
            position: "relative",
            margin: "0 auto"
          }}
        >
          {/* 선택 영역 하이라이트 */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: 0,
              right: 0,
              height: ITEM_HEIGHT,
              transform: "translateY(-50%)",
              background: "#F2F4F8",
              borderRadius: "8px",
              pointerEvents: "none",
              zIndex: 0
            }}
          />

          <PickerColumn
            options={yearOptions}
            value={value.year}
            onChange={(v) => setValue((prev) => ({ ...prev, year: v }))}
          />
          <PickerColumn
            options={monthOptions}
            value={value.month}
            onChange={(v) => setValue((prev) => ({ ...prev, month: v }))}
          />
          <PickerColumn
            options={dayOptions}
            value={value.day}
            onChange={(v) => setValue((prev) => ({ ...prev, day: v }))}
          />
        </div>
      </div>

      <div className="bottom-box" style={{ width: "100%" }}>
        <div className="inner-box">
          <div className="btn-layout" data-btn-num="2">
            <button
              type="button"
              className="btn"
              data-btn-type="outline-main"
              data-btn-size="lg"
              onClick={onClose}
              aria-label="날짜 선택 닫기"
              ref={closeButtonRef}
            >
              <span>취소</span>
            </button>
            <button
              type="button"
              className="btn"
              data-btn-type="fill-main"
              data-btn-size="lg"
              onClick={handleConfirm}
              aria-label="날짜 선택 완료"
            >
              <span>선택완료</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomDatePicker;